import { clientState } from '../services/clientState';
// audit-safety-integrated
import { readJson } from '../utils/auditSafety';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from '../services/api';

const emptySupplier = () => ({ company: '', contact_person: '', contact_number: '', address: '' });

const parseRate = (raw) => {
  if (raw === undefined || raw === null || raw === '-') return { rate: null, suppliers: [] };
  if (typeof raw === 'object') return { rate: raw.rate ?? null, suppliers: raw.suppliers || [] };
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return { rate: parsed.rate ?? null, suppliers: parsed.suppliers || [] };
    } catch {
      const n = parseFloat(raw);
      return { rate: isNaN(n) ? null : n, suppliers: [] };
    }
  }
  const n = parseFloat(raw);
  return { rate: isNaN(n) ? null : n, suppliers: [] };
};

const PriceUpdation = () => {
  const navigate = useNavigate();
  const location = useLocation();


  const readonlyView = location.state?.readonly === true;
  const overrideJpcType = location.state?.jpcType || null;
  const overrideDistrict = location.state?.district || null;
  const overrideItems = location.state?.items || null;
  const overrideUserName = location.state?.userName || null;

  const [uniqueItems, setUniqueItems]             = useState([]);
  const [districts, setDistricts]                 = useState([]);
  const [isState, setIsState]                     = useState(false);
  const [loading, setLoading]                     = useState(false);
  const [pricesMap, setPricesMap]                 = useState({});   // { itemId: "12.50" }
  const [suppliersMap, setSuppliersMap]           = useState({});   // { itemId: [supplier,...] }
  const [dirtyIds, setDirtyIds]                   = useState(new Set());
  const [editingId, setEditingId]                 = useState(null);
  const [editAllMode, setEditAllMode]             = useState(false);
  const [supplierModal, setSupplierModal]         = useState(null);
  const [editingSupplierIdx, setEditingSupplierIdx] = useState(null);
  const [savingAll, setSavingAll]                 = useState(false);
  const [success, setSuccess]                     = useState('');
  const [error, setError]                         = useState('');
  const [itemSearch, setItemSearch]               = useState('');
  const [trendData, setTrendData]                 = useState({});   // { [itemId]: [{ date, rate }, ...] }
  const [loadingTrends, setLoadingTrends]         = useState({});   // { [itemId]: true/false }
  const [trendModal, setTrendModal]               = useState(null); // { itemId, itemName }
  const [trendPeriod, setTrendPeriod]             = useState('all'); // '7d' | '1m' | '3m' | '6m' | 'all'
  const [tablePeriod, setTablePeriod]             = useState('all');

  // Supplier master state
  const [masterSuppliers, setMasterSuppliers]     = useState([]);   // [{ name, contact, address }, ...]
  const [masterId, setMasterId]                   = useState(null); // master record id (for PUT)
  const [showMasterModal, setShowMasterModal]     = useState(false);
  const [masterDraft, setMasterDraft]             = useState([]);   // editable copy in master modal
  const [savingMaster, setSavingMaster]           = useState(false);
  const [loadingMaster, setLoadingMaster]         = useState(false);

  // Role check — hide master features from rcs-admin
  const currentUserRole = (() => {
    try { return JSON.parse(clientState.getItem('user') || '{}')?.role || ''; } catch { return ''; }
  })();
  const showMasterFeature = !readonlyView && currentUserRole !== 'rcs-admin';

  const getUserData = () => {
    const userData = clientState.getItem('user');
    const loginResponse = clientState.getItem('loginResponse');
    return userData ? JSON.parse(userData) : loginResponse ? JSON.parse(loginResponse) : null;
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!showMasterFeature) return;
    fetchMasterSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMasterSuppliers = async () => {
    setLoadingMaster(true);
    try {
      const res = await fetch(`${API_BASE_URL}/master/jpc-sup`, {
        headers: {  },
      });
      const data = await readJson(res);
      if (!data.error) {
        // Response: { data: [{ id, name, suppliers: [...] }, ...] } — take the first record
        const record = Array.isArray(data?.data)
          ? (data.data[0] || null)
          : (data?.data || data);
        const list = record?.suppliers || [];
        const id = record?.id ?? null;
        setMasterSuppliers(Array.isArray(list) ? list : []);
        setMasterId(id);
      }
    } catch (err) {
      void 0;
    } finally {
      setLoadingMaster(false);
    }
  };

  const saveMasterSuppliers = async () => {
    // Validate phone numbers (must be exactly 10 digits if provided)
    const invalid = masterDraft.find(s => {
      const c = (s.contact || '').trim();
      return c.length > 0 && !/^\d{10}$/.test(c);
    });
    if (invalid) {
      setError('Each supplier contact must be exactly 10 digits.');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setSavingMaster(true);
    try {
      const cleaned = masterDraft
        .filter(s => (s.name || '').trim() || (s.contact || '').trim() || (s.contact_person || '').trim())
        .map(s => ({
          name: (s.name || '').trim(),
          contact_person: (s.contact_person || '').trim(),
          contact: (s.contact || '').trim(),
          address: (s.address || '').trim(),
        }));

      // PUT to update existing master record, POST if first time
      const isUpdate = masterId !== null && masterId !== undefined;
      const url = isUpdate
        ? `${API_BASE_URL}/master/jpc-sup/${masterId}`
        : `${API_BASE_URL}/master/jpc-sup`;
      const method = isUpdate ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {  'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'supplier', suppliers: cleaned }),
      });
      const data = await readJson(res);
      if (!data.error) {
        setMasterSuppliers(cleaned);
        // Capture id from create response if it was a POST
        if (!isUpdate) {
          const newId = data?.data?.id ?? data?.id ?? null;
          if (newId) setMasterId(newId);
        }
        setShowMasterModal(false);
        setSuccess('Supplier master saved');
      } else {
        setError(data.message || 'Failed to save supplier master');
      }
    } catch (err) {
      setError('Failed to save supplier master');
    } finally {
      setSavingMaster(false);
    }
  };

  const openMasterModal = () => {
    // start with a copy; if empty, seed with one blank row
    setMasterDraft(masterSuppliers.length > 0
      ? masterSuppliers.map(s => ({
          name: s.name || '',
          contact: s.contact || '',
          contact_person: s.contact_person || '',
          address: s.address || '',
        }))
      : [{ name: '', contact: '', contact_person: '', address: '' }]
    );
    setShowMasterModal(true);
  };

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      let stateUser, itemIdsForState, districtListForRegional;

      if (readonlyView) {
        stateUser = overrideJpcType === 'state';
        itemIdsForState = overrideItems || [];
        districtListForRegional = overrideDistrict ? [overrideDistrict] : [];
      } else {
        const parsed = getUserData();
        stateUser = parsed?.others?.jpc_type === 'state' || parsed?.detail?.jpc_type === 'state';
        itemIdsForState = parsed?.others?.items || parsed?.detail?.items || [];
        let raw = parsed?.others?.districts || parsed?.detail?.districts || null;
        if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = {}; } }
        districtListForRegional = raw ? (Array.isArray(raw) ? raw : Object.keys(raw)) : [];
      }
      setIsState(stateUser);

      if (stateUser) {
        const itemIds = itemIdsForState;
        const [detailsRes, ratesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/item/${itemIds.join(',')}/selected`, { headers: {  } }),
          fetch(`${API_BASE_URL}/item/jpc-rate/state`, { headers: {  } }),
        ]);
        const detailsData = await readJson(detailsRes);
        const ratesData   = await readJson(ratesRes);

        const rateMap = {};
        (ratesData.data || []).forEach(r => { rateMap[r.id] = r.detail !== undefined ? r.detail : r.rate; });

        const list = detailsData.data || [];
        setUniqueItems(list);
        setDistricts(['state']);

        const preFillPrices = {}, preFillSuppliers = {};
        list.forEach(item => {
          const { rate, suppliers } = parseRate(rateMap[item.id]);
          if (rate !== null) preFillPrices[item.id] = Number(rate).toFixed(2);
          if (suppliers.length) preFillSuppliers[item.id] = suppliers;
        });
        setPricesMap(preFillPrices);
        setSuppliersMap(preFillSuppliers);
      } else {
        const districtList = districtListForRegional;
        if (!districtList || districtList.length === 0) { setUniqueItems([]); return; }
        setDistricts(districtList);

        // 1. Fetch rates for the district
        const firstResult = await fetch(
          `${API_BASE_URL}/item/jpc-rate/${encodeURIComponent(districtList[0])}`,
          { headers: {  } }
        ).then(r => readJson(r));

        const rateRows = firstResult.data || [];
        void 0;
        const itemIds = rateRows.map(r => r.id).filter(Boolean);

        // 2. Fetch full item details in parallel using ids — same pattern as state path
        let itemDetails = [];
        if (itemIds.length > 0) {
          const detailsRes = await fetch(
            `${API_BASE_URL}/item/${itemIds.join(',')}/selected`,
            { headers: {  } }
          );
          const detailsData = await readJson(detailsRes);
          itemDetails = detailsData.data || [];
        }

        // Merge: take name/unit/indent from /selected, KEEP rate+suppliers from rate row
        const detailsById = Object.fromEntries(itemDetails.map(it => [String(it.id), it]));
        const merged = rateRows.map(r => {
          const d = detailsById[String(r.id)] || {};
          const raw = r.detail !== undefined ? r.detail : r.rate;
          return {
            ...d,
            id: r.id,
            rate: raw,
          };
        });
        setUniqueItems(merged);

        const preFillPrices = {}, preFillSuppliers = {};
        rateRows.forEach(r => {
          const raw = r.detail !== undefined ? r.detail : r.rate;
          const { rate, suppliers } = parseRate(raw);
          if (rate !== null) preFillPrices[r.id] = Number(rate).toFixed(2);
          if (suppliers.length) preFillSuppliers[r.id] = suppliers;
        });
        setPricesMap(preFillPrices);
        setSuppliersMap(preFillSuppliers);
      }
    } catch (err) {
      void 0;
      setError('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (itemId, value) => {
    setPricesMap(prev => ({ ...prev, [itemId]: value }));
    setDirtyIds(prev => new Set(prev).add(itemId));
  };

  const handlePriceBlur = (itemId) => {
    setPricesMap(prev => {
      const val = prev[itemId];
      if (val === '' || val === undefined) return prev;
      const num = parseFloat(val);
      if (isNaN(num)) return prev;
      return { ...prev, [itemId]: num.toFixed(2) };
    });
  };

  const openSupplierModal = (item) => {
    // Always start fresh — never preload from existing rate API data
    setSuppliersMap(prev => ({ ...prev, [item.id]: [emptySupplier()] }));
    setEditingSupplierIdx(0);
    setSupplierModal({ itemId: item.id, itemName: item.name });
  };

  const fetchItemTrend = async (itemId) => {
    setLoadingTrends(prev => ({ ...prev, [itemId]: true }));
    try {
      const scope = isState ? 'state' : (districts[0] ? encodeURIComponent(districts[0]) : 'state');
      const res = await fetch(`${API_BASE_URL}/item/jpc-rate/${scope}/${itemId}`, {
        headers: {  },
      });
      const data = await readJson(res);
      const logs = !data.error && Array.isArray(data.data) ? data.data : [];
      const parseDate = (raw) => {
        if (!raw) return NaN;
        // Convert "YYYY-MM-DD HH:mm:ss" → "YYYY-MM-DDTHH:mm:ss" for cross-browser parsing
        const iso = typeof raw === 'string' ? raw.replace(' ', 'T') : raw;
        return new Date(iso).getTime();
      };
      const points = logs
        .map(l => {
          const d = l.created_at || l.updated_at || l.date;
          const r = l.rate ?? l.price;
          const ts = parseDate(d);
          return d && r != null && !isNaN(ts) ? { date: d, ts, rate: Number(r) } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.ts - b.ts);
      setTrendData(prev => ({ ...prev, [itemId]: points }));
    } catch (err) {
      void 0;
      setTrendData(prev => ({ ...prev, [itemId]: [] }));
    } finally {
      setLoadingTrends(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Lazy-load trends for all visible items once item list is ready
  useEffect(() => {
    if (uniqueItems.length === 0) return;
    uniqueItems.forEach(item => {
      if (trendData[item.id] === undefined && !loadingTrends[item.id]) {
        fetchItemTrend(item.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueItems]);

  const addSupplier = (itemId) => {
    setSuppliersMap(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), emptySupplier()] }));
    setDirtyIds(prev => new Set(prev).add(itemId));
  };

  const removeSupplier = (itemId, idx) => {
    setSuppliersMap(prev => {
      const list = [...(prev[itemId] || [])];
      list.splice(idx, 1);
      return { ...prev, [itemId]: list.length ? list : [emptySupplier()] };
    });
    setDirtyIds(prev => new Set(prev).add(itemId));
  };

  const updateSupplier = (itemId, idx, field, value) => {
    setSuppliersMap(prev => {
      const list = [...(prev[itemId] || [])];
      list[idx] = { ...list[idx], [field]: value };
      return { ...prev, [itemId]: list };
    });
    setDirtyIds(prev => new Set(prev).add(itemId));
  };

  // Save: apply the same price & supplier to all districts
  const handleSaveAll = async () => {
    if (dirtyIds.size === 0) return;

    // Block save if any dirty item has a price but no supplier
    const missing = [];
    dirtyIds.forEach(itemId => {
      const val = pricesMap[itemId];
      const hasPrice = val !== undefined && val !== '' && !isNaN(parseFloat(val));
      const hasSupplier = (suppliersMap[itemId] || []).some(s => s.company || s.contact_person || s.contact_number);
      if (hasPrice && !hasSupplier) {
        const it = uniqueItems.find(u => u.id === itemId);
        missing.push(it ? it.name.split('/')[0].trim() : `Item #${itemId}`);
      }
    });
    if (missing.length > 0) {
      setError(`Please add at least one supplier for: ${missing.join(', ')}`);
      setTimeout(() => setError(''), 5000);
      return;
    }

    setSavingAll(true);
    setSuccess(''); setError('');
    try {
      const districtTargets = isState ? ['state'] : districts;

      await Promise.all(
        districtTargets.map(district => {
          const items = {};
          dirtyIds.forEach(itemId => {
            const val = pricesMap[itemId];
            const price = val !== undefined && val !== '' ? parseFloat(val) : null;
            const suppliers = (suppliersMap[itemId] || []).filter(s => s.company || s.contact_person || s.contact_number);
            items[itemId] = {
              ...(price !== null && !isNaN(price) ? { rate: price } : {}),
              suppliers,
            };
          });
          return fetch(`${API_BASE_URL}/item/jpc-rate/${encodeURIComponent(district)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
        })
      );

      setPricesMap(prev => {
        const next = { ...prev };
        dirtyIds.forEach(id => {
          if (next[id]) next[id] = parseFloat(next[id]).toFixed(2);
        });
        return next;
      });
      const count = dirtyIds.size;
      setDirtyIds(new Set());
      setEditingId(null);
      setSuccess(`Saved ${count} item${count > 1 ? 's' : ''} successfully`);
    } catch {
      setError('Failed to save');
    } finally {
      setSavingAll(false);
    }
  };

  const filteredItems = itemSearch.trim()
    ? uniqueItems.filter(i => i.name.toLowerCase().includes(itemSearch.toLowerCase()))
    : uniqueItems;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {readonlyView && (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-[#1978B5] hover:border-[#1978B5] transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              {readonlyView ? 'Rates' : 'Price Updation'}
              {readonlyView && overrideUserName && (
                <span className="ml-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
                  {overrideUserName}
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500">
              {readonlyView ? 'View only — rates assigned to this JPC user.' : 'Update prices and suppliers for your assigned items'}
            </p>
          </div>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {success}
            <button onClick={() => setSuccess('')} className="ml-auto">✕</button>
          </div>
        )}
        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
            <button onClick={() => setError('')} className="ml-auto">✕</button>
          </div>
        )}

        {/* Districts — informational display only */}
        {!isState && districts.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Districts</p>
            <div className="flex flex-wrap gap-2">
              {districts.map(d => (
                <span
                  key={d}
                  className="text-xs px-3 py-1.5 rounded-full font-medium bg-blue-50 text-blue-600 border border-blue-100"
                >
                  {d}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">

          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 flex items-center gap-3">
            <div className="relative flex-1">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search items..."
                value={itemSearch}
                onChange={e => setItemSearch(e.target.value)}
                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
              />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 mr-1">Range:</span>
              {[
                { key: '7d', label: '7D' },
                { key: '1m', label: '1M' },
                { key: '3m', label: '3M' },
                { key: '6m', label: '6M' },
                { key: 'all', label: 'All' },
              ].map(p => (
                <button
                  key={p.key}
                  onClick={() => setTablePeriod(p.key)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                    tablePeriod === p.key
                      ? 'bg-[#1978B5] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {showMasterFeature && (
              <button
                onClick={openMasterModal}
                className="flex items-center gap-2 border border-[#1978B5] text-[#1978B5] hover:bg-blue-50 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                title="Manage Supplier Master"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Suppliers ({masterSuppliers.length})
              </button>
            )}
            <button
              onClick={async () => {
                if (filteredItems.length === 0) return;
                const wb = new ExcelJS.Workbook();
                const ws = wb.addWorksheet('Price Updation');
                const DATE_FMT = 'dd-mmm-yyyy';
                ws.columns = [
                  { header: '#', key: 'idx', width: 5 },
                  { header: 'Item Name', key: 'name', width: 36 },
                  { header: 'Unit', key: 'unit', width: 8 },
                  { header: 'Current Rate (₹)', key: 'rate', width: 14 },
                  { header: 'Current Rate Updated On', key: 'rateDate', width: 22, style: { numFmt: DATE_FMT } },
                  { header: 'Prev 1', key: 'p1', width: 12 },
                  { header: 'Prev 1 Updated On', key: 'p1Date', width: 20, style: { numFmt: DATE_FMT } },
                  { header: 'Prev 2', key: 'p2', width: 12 },
                  { header: 'Prev 2 Updated On', key: 'p2Date', width: 20, style: { numFmt: DATE_FMT } },
                  { header: 'Prev 3', key: 'p3', width: 12 },
                  { header: 'Prev 3 Updated On', key: 'p3Date', width: 20, style: { numFmt: DATE_FMT } },
                  { header: 'Prev 4', key: 'p4', width: 12 },
                  { header: 'Prev 4 Updated On', key: 'p4Date', width: 20, style: { numFmt: DATE_FMT } },
                  { header: 'Suppliers', key: 'sup', width: 50 },
                ];
                const head = ws.getRow(1);
                head.font = { bold: true };
                head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
                head.alignment = { vertical: 'middle', horizontal: 'center' };

                const toDate = (raw) => {
                  if (!raw) return null;
                  const iso = typeof raw === 'string' ? raw.replace(' ', 'T') : raw;
                  const dt = new Date(iso);
                  return isNaN(dt.getTime()) ? null : dt;
                };

                const periodMs = { '7d': 7*864e5, '1m': 30*864e5, '3m': 90*864e5, '6m': 180*864e5 };
                const nowMs = Date.now();
                filteredItems.forEach((item, i) => {
                  const trendAll = trendData[item.id] || [];
                  const trend = tablePeriod === 'all'
                    ? trendAll
                    : trendAll.filter(l => l.ts && (nowMs - l.ts) <= periodMs[tablePeriod]);
                  const sorted = [...trend].sort((a, b) => b.ts - a.ts);
                  // Newest entry → "Current"; next 4 → Prev 1..4
                  const current = sorted[0];
                  const prev = sorted.slice(1, 5);
                  const sups = (suppliersMap[item.id] || [])
                    .filter(s => s.company || s.contact_person || s.contact_number)
                    .map(s => [s.company, s.contact_person, s.contact_number, s.address].filter(Boolean).join(' · '))
                    .join(' | ');
                  ws.addRow({
                    idx: i + 1,
                    name: item.name.split('/')[0].trim(),
                    unit: item.unit || item.indent || '',
                    rate: pricesMap[item.id] ?? (current ? current.rate : ''),
                    rateDate: current ? toDate(current.date) : null,
                    p1: prev[0] ? prev[0].rate : '',
                    p1Date: prev[0] ? toDate(prev[0].date) : null,
                    p2: prev[1] ? prev[1].rate : '',
                    p2Date: prev[1] ? toDate(prev[1].date) : null,
                    p3: prev[2] ? prev[2].rate : '',
                    p3Date: prev[2] ? toDate(prev[2].date) : null,
                    p4: prev[3] ? prev[3].rate : '',
                    p4Date: prev[3] ? toDate(prev[3].date) : null,
                    sup: sups,
                  });
                });

                ws.eachRow((row) => {
                  row.eachCell((cell) => {
                    cell.border = {
                      top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                      left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                      bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                      right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                    };
                  });
                });

                const buf = await wb.xlsx.writeBuffer();
                const stamp = new Date().toISOString().slice(0, 10);
                saveAs(new Blob([buf]), `price-updation_${stamp}.xlsx`);
              }}
              disabled={filteredItems.length === 0}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filteredItems.length === 0
                  ? 'border border-gray-200 text-gray-400 cursor-not-allowed'
                  : 'border border-emerald-600 text-emerald-700 hover:bg-emerald-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
              Export Excel
            </button>
            {!readonlyView && !editAllMode && (
              <button
                onClick={() => setEditAllMode(true)}
                className="flex items-center gap-2 bg-[#1978B5] hover:bg-[#146699] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
              </button>
            )}
            {!readonlyView && editAllMode && (
              <>
                <button
                  onClick={() => { setEditAllMode(false); setEditingId(null); }}
                  disabled={savingAll}
                  className="flex items-center gap-2 border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => { await handleSaveAll(); setEditAllMode(false); }}
                  disabled={savingAll || dirtyIds.size === 0}
                  className="flex items-center gap-2 bg-[#1978B5] hover:bg-[#146699] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {savingAll ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {savingAll ? 'Saving...' : `Save${dirtyIds.size > 0 ? ` (${dirtyIds.size})` : ''}`}
                </button>
              </>
            )}
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <svg className="w-6 h-6 animate-spin text-[#1978B5]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No items found</div>
          ) : (
            <div className="overflow-hidden">
              <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <div className="col-span-1">#</div>
                <div className={readonlyView ? 'col-span-6' : 'col-span-3'}>Item Name</div>
                <div className="col-span-1 text-center">Price (₹)</div>
                <div className="col-span-1 text-center">Prev 1</div>
                <div className="col-span-1 text-center">Prev 2</div>
                <div className="col-span-1 text-center">Prev 3</div>
                <div className="col-span-1 text-center">Prev 4</div>
                {!readonlyView && !editAllMode && <div className="col-span-3 text-center">Suppliers</div>}
                {!readonlyView && editAllMode && <div className="col-span-3 text-center">&nbsp;</div>}
              </div>

              <div className="divide-y divide-gray-50 max-h-[600px] overflow-y-auto">
                {filteredItems.map((item, idx) => {
                  const isEditing    = editAllMode || editingId === item.id;
                  const val          = pricesMap[item.id] ?? '';
                  const hasRate      = val !== '' && val !== undefined;
                  const isDirty      = dirtyIds.has(item.id);
                  const hasSuppliers = (suppliersMap[item.id] || []).some(s => s.company || s.contact_person || s.contact_number);

                  return (
                    <div key={item.id}>
                    <div
                      className={`grid grid-cols-12 items-center px-4 py-3 transition-colors
                        ${isDirty ? 'bg-yellow-50' : hasRate ? 'bg-green-50/40' : 'hover:bg-gray-50'}`}
                    >
                      <div className="col-span-1 text-xs text-gray-400">{idx + 1}</div>

                      <div className={`${readonlyView ? 'col-span-6' : 'col-span-3'} pr-2`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-gray-800 leading-snug">{item.name.split('/')[0].trim()}</p>
                          {(item.unit || item.indent) && (
                            <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                              {item.unit || item.indent}
                            </span>
                          )}
                        </div>
                        {item.name.includes('/') && (
                          <p className="text-xs text-gray-400">{item.name.split('/')[1]?.trim()}</p>
                        )}
                      </div>

                      <div className="col-span-1 flex justify-center">
                        {isEditing ? (
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={val}
                              onChange={e => handlePriceChange(item.id, e.target.value)}
                              onBlur={() => handlePriceBlur(item.id)}
                              placeholder="0.00"
                              autoFocus={!editAllMode}
                              className="w-20 border border-yellow-300 rounded-lg pl-5 pr-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#1978B5] bg-white"
                            />
                          </div>
                        ) : (
                          <span className={`text-sm font-medium ${hasRate ? 'text-gray-800' : 'text-gray-300'}`}>
                            {hasRate ? `₹ ${val}` : '—'}
                          </span>
                        )}
                      </div>

                      {(() => {
                        const trendAll = trendData[item.id] || [];
                        const periodMs = { '7d': 7*864e5, '1m': 30*864e5, '3m': 90*864e5, '6m': 180*864e5 };
                        const now = Date.now();
                        const trend = tablePeriod === 'all'
                          ? trendAll
                          : trendAll.filter(l => l.ts && (now - l.ts) <= periodMs[tablePeriod]);
                        // Latest 4 entries (newest first), excluding the current/most recent shown in Price column
                        const sorted = [...trend].sort((a, b) => b.ts - a.ts);
                        const prev = sorted.slice(0, 4);
                        const onCellClick = () => {
                          if (trend.length > 0) {
                            setTrendPeriod('all');
                            setTrendModal({ itemId: item.id, itemName: item.name });
                          }
                        };
                        const formatDate = (d) => {
                          if (!d) return '';
                          const iso = typeof d === 'string' ? d.replace(' ', 'T') : d;
                          const dt = new Date(iso);
                          return isNaN(dt.getTime())
                            ? String(d)
                            : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                        };
                        return [0, 1, 2, 3].map((i) => {
                          const entry = prev[i];
                          return (
                            <div
                              key={i}
                              onClick={onCellClick}
                              className="col-span-1 flex flex-col items-center justify-center px-1 cursor-pointer hover:bg-blue-50/40 rounded transition-colors"
                              title={trend.length > 0 ? 'Click to view full history' : ''}
                            >
                              {loadingTrends[item.id] ? (
                                i === 0 ? (
                                  <svg className="w-4 h-4 animate-spin text-[#1978B5]" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                  </svg>
                                ) : null
                              ) : entry ? (
                                <>
                                  <span className="text-sm font-medium text-gray-700">₹ {entry.rate}</span>
                                  <span className="text-[10px] text-gray-400">{formatDate(entry.date)}</span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </div>
                          );
                        });
                      })()}

                      {!readonlyView && !editAllMode && (
                        <div className="col-span-3 flex flex-col items-center justify-center text-xs text-gray-600">
                          {hasSuppliers ? (
                            <div className="text-center max-w-[180px]">
                              {(suppliersMap[item.id] || [])
                                .filter(s => s.company || s.contact_person)
                                .map((s, i) => (
                                  <div key={i} className="leading-tight truncate">
                                    <span className="font-medium text-gray-700">{s.company || s.contact_person}</span>
                                    {s.contact_number && <span className="text-gray-400 ml-1">· {s.contact_number}</span>}
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </div>
                      )}
                      {!readonlyView && editAllMode && (
                        <div className="col-span-3" />
                      )}
                    </div>

                    {!readonlyView && editAllMode && (
                      <div className="bg-orange-50/50 border-b border-orange-100 px-6 py-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Suppliers for this item</p>
                          <button
                            onClick={() => addSupplier(item.id)}
                            className="text-xs text-[#1978B5] hover:text-[#155f91] font-medium"
                          >
                            + Add Supplier
                          </button>
                        </div>
                        {(suppliersMap[item.id] || []).length === 0 && (
                          <p className="text-xs text-gray-400 italic">No suppliers added.</p>
                        )}
                        {masterSuppliers.length === 0 && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                            No suppliers in master. Add suppliers via the "Suppliers" button above first.
                          </p>
                        )}
                        {(suppliersMap[item.id] || []).map((sup, sIdx) => {
                          const matchedIdx = masterSuppliers.findIndex(m =>
                            (m.name || '') === (sup.company || '') &&
                            (m.contact || '') === (sup.contact_number || '')
                          );
                          return (
                            <div key={sIdx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                              <select
                                value={matchedIdx >= 0 ? String(matchedIdx) : ''}
                                onChange={(e) => {
                                  const sel = masterSuppliers[Number(e.target.value)];
                                  if (!sel) return;
                                  updateSupplier(item.id, sIdx, 'company', sel.name || '');
                                  updateSupplier(item.id, sIdx, 'contact_person', sel.contact_person || '');
                                  updateSupplier(item.id, sIdx, 'contact_number', sel.contact || '');
                                  updateSupplier(item.id, sIdx, 'address', sel.address || '');
                                }}
                                disabled={masterSuppliers.length === 0}
                                className="col-span-5 border border-gray-200 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#1978B5] disabled:bg-gray-100"
                              >
                                <option value="">— Select supplier from master —</option>
                                {masterSuppliers.map((s, i) => (
                                  <option key={i} value={i}>
                                    {s.name || '(unnamed)'}{s.contact ? ` · ${s.contact}` : ''}
                                  </option>
                                ))}
                              </select>
                              <div className="col-span-6 text-xs text-gray-600">
                                {sup.company ? (
                                  <>
                                    <span className="font-medium text-gray-700">{sup.company}</span>
                                    {sup.contact_person && <span> · {sup.contact_person}</span>}
                                    {sup.contact_number && <span className="text-gray-400"> · {sup.contact_number}</span>}
                                    {sup.address && <span className="text-gray-400"> · {sup.address}</span>}
                                  </>
                                ) : (
                                  <span className="text-gray-400 italic">Not selected</span>
                                )}
                              </div>
                              <button
                                onClick={() => removeSupplier(item.id, sIdx)}
                                title="Remove supplier"
                                className="col-span-1 flex justify-center items-center text-red-500 hover:text-red-700 py-1.5"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                </svg>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Supplier Modal */}
        {supplierModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Suppliers</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{supplierModal.itemName.split('/')[0].trim()}</p>
                </div>
                <button onClick={() => { setSupplierModal(null); setEditingSupplierIdx(null); }} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
                {(suppliersMap[supplierModal.itemId] || []).map((supplier, idx) => {
                  const isEditingThis = editingSupplierIdx === idx;
                  const hasData = supplier.company || supplier.contact_person || supplier.contact_number;
                  const allSuppliers = suppliersMap[supplierModal.itemId] || [];
                  const isDuplicateNumber = supplier.contact_number &&
                    allSuppliers.some((s, i) => i !== idx && s.contact_number === supplier.contact_number);
                  return (
                    <div key={idx} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier {idx + 1}</span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingSupplierIdx(isEditingThis ? null : idx)}
                            className={`text-xs px-2.5 py-1 rounded-lg font-medium transition-all
                              ${isEditingThis ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' : 'bg-blue-50 text-[#1978B5] hover:bg-blue-100'}`}
                          >
                            {isEditingThis ? 'Done' : 'Edit'}
                          </button>
                          {allSuppliers.length > 1 && (
                            <button onClick={() => removeSupplier(supplierModal.itemId, idx)} className="text-xs text-red-400 hover:text-red-600">
                              Remove
                            </button>
                          )}
                        </div>
                      </div>

                      {isEditingThis ? (
                        <div className="space-y-3">
                          {showMasterFeature && masterSuppliers.length > 0 && (
                            <div className="p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                              <label className="block text-xs font-medium text-blue-700 mb-1">Pick from Supplier Master</label>
                              <select
                                className="w-full border border-blue-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                                value=""
                                onChange={(e) => {
                                  const sel = masterSuppliers[Number(e.target.value)];
                                  if (!sel) return;
                                  updateSupplier(supplierModal.itemId, idx, 'company', sel.name || '');
                                  updateSupplier(supplierModal.itemId, idx, 'contact_person', sel.contact_person || '');
                                  updateSupplier(supplierModal.itemId, idx, 'contact_number', sel.contact || '');
                                  updateSupplier(supplierModal.itemId, idx, 'address', sel.address || '');
                                }}
                              >
                                <option value="">— Select to autofill —</option>
                                {masterSuppliers.map((s, i) => (
                                  <option key={i} value={i}>{s.name || '(unnamed)'}{s.contact ? ` · ${s.contact}` : ''}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
                              <input
                                type="text"
                                value={supplier.company}
                                onChange={e => updateSupplier(supplierModal.itemId, idx, 'company', e.target.value)}
                                placeholder="e.g. ABC Traders"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Contact Person</label>
                              <input
                                type="text"
                                value={supplier.contact_person}
                                onChange={e => updateSupplier(supplierModal.itemId, idx, 'contact_person', e.target.value)}
                                placeholder="e.g. Ravi Kumar"
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Contact Number</label>
                              <input
                                type="tel"
                                value={supplier.contact_number}
                                onChange={e => updateSupplier(supplierModal.itemId, idx, 'contact_number', e.target.value)}
                                placeholder="e.g. 9876543210"
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2
                                  ${isDuplicateNumber ? 'border-red-400 focus:ring-red-300 bg-red-50' : 'border-gray-300 focus:ring-[#1978B5]'}`}
                              />
                              {isDuplicateNumber && (
                                <p className="text-xs text-red-500 mt-1">Number already used by another supplier</p>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                            <input
                              type="text"
                              value={supplier.address}
                              onChange={e => updateSupplier(supplierModal.itemId, idx, 'address', e.target.value)}
                              placeholder="e.g. 12, Main Road, Chennai"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                            />
                          </div>
                        </div>
                      ) : hasData ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Company Name</p>
                              <p className="text-sm font-medium text-gray-800">{supplier.company || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Contact Person</p>
                              <p className="text-sm font-medium text-gray-800">{supplier.contact_person || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Contact Number</p>
                              <p className="text-sm font-medium text-gray-800">{supplier.contact_number || '—'}</p>
                            </div>
                          </div>
                          {supplier.address && (
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Address</p>
                              <p className="text-sm font-medium text-gray-800">{supplier.address}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400 italic">No details added. Click Edit to fill in.</p>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={() => { addSupplier(supplierModal.itemId); setEditingSupplierIdx((suppliersMap[supplierModal.itemId] || []).length); }}
                  className="w-full border border-dashed border-gray-300 text-gray-500 hover:border-[#1978B5] hover:text-[#1978B5] rounded-xl py-2.5 text-sm font-medium transition-all"
                >
                  + Add Another Supplier
                </button>
              </div>

              <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
                {(() => {
                  const list = suppliersMap[supplierModal.itemId] || [];
                  const numbers = list.map(s => s.contact_number).filter(Boolean);
                  const hasDuplicates = numbers.length !== new Set(numbers).size;
                  return (
                    <>
                      {hasDuplicates && (
                        <p className="text-xs text-red-500 text-center mb-2">Fix duplicate contact numbers before saving</p>
                      )}
                      <button
                        disabled={hasDuplicates}
                        onClick={() => { setSupplierModal(null); setEditingSupplierIdx(null); }}
                        className={`w-full py-2 rounded-lg text-sm font-medium transition-all
                          ${hasDuplicates ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#1978B5] hover:bg-[#146699] text-white'}`}
                      >
                        Done
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Supplier Master Modal */}
        {showMasterModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div>
                  <h3 className="text-base font-semibold text-gray-800">Supplier Master</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Manage suppliers — used as quick-pick when adding suppliers per item.</p>
                </div>
                <button onClick={() => setShowMasterModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-2">
                {masterDraft.map((s, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Supplier {idx + 1}</span>
                      <button
                        onClick={() => setMasterDraft(prev => prev.filter((_, i) => i !== idx))}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                        <input
                          type="text"
                          value={s.name || ''}
                          onChange={e => setMasterDraft(prev => prev.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Contact Person</label>
                        <input
                          type="text"
                          value={s.contact_person || ''}
                          onChange={e => setMasterDraft(prev => prev.map((x, i) => i === idx ? { ...x, contact_person: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                        <input
                          type="tel"
                          inputMode="numeric"
                          maxLength={10}
                          value={s.contact || ''}
                          onChange={e => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setMasterDraft(prev => prev.map((x, i) => i === idx ? { ...x, contact: digits } : x));
                          }}
                          className={`w-full border rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1978B5] ${
                            (s.contact || '').length > 0 && (s.contact || '').length !== 10
                              ? 'border-red-400 bg-red-50'
                              : 'border-gray-300'
                          }`}
                        />
                        {(s.contact || '').length > 0 && (s.contact || '').length !== 10 && (
                          <p className="text-[11px] text-red-600 mt-0.5">Must be exactly 10 digits</p>
                        )}
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                        <input
                          type="text"
                          value={s.address || ''}
                          onChange={e => setMasterDraft(prev => prev.map((x, i) => i === idx ? { ...x, address: e.target.value } : x))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1978B5]"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setMasterDraft(prev => [...prev, { name: '', contact: '', contact_person: '', address: '' }])}
                  className="w-full border border-dashed border-gray-300 text-gray-500 hover:border-[#1978B5] hover:text-[#1978B5] rounded-xl py-2.5 text-sm font-medium transition-all"
                >
                  + Add Supplier
                </button>
              </div>

              <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowMasterModal(false)}
                  className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveMasterSuppliers}
                  disabled={savingMaster}
                  className="px-4 py-1.5 text-sm bg-[#1978B5] hover:bg-[#146699] text-white rounded-lg disabled:opacity-60 flex items-center gap-2"
                >
                  {savingMaster && (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  )}
                  {savingMaster ? 'Saving...' : 'Save Master'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Price Trend Logs Modal */}
        {trendModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[85vh]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
                <div className="min-w-0 pr-3">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Price Trend</p>
                  <h3 className="text-lg font-semibold text-gray-800 truncate">{trendModal.itemName.split('/')[0].trim()}</h3>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={async () => {
                      const allLogs = trendData[trendModal.itemId] || [];
                      const periodMs = { '7d': 7*864e5, '1m': 30*864e5, '3m': 90*864e5, '6m': 180*864e5 };
                      const now = Date.now();
                      const logs = trendPeriod === 'all'
                        ? allLogs
                        : allLogs.filter(l => l.ts && (now - l.ts) <= periodMs[trendPeriod]);
                      if (logs.length === 0) return;
                      const itemName = trendModal.itemName.split('/')[0].trim();

                      const wb = new ExcelJS.Workbook();
                      const ws = wb.addWorksheet('Price Trend');
                      ws.columns = [
                        { header: 'Date', key: 'date', width: 22 },
                        { header: 'Rate (₹)', key: 'rate', width: 14 },
                      ];
                      const headerRow = ws.getRow(1);
                      headerRow.font = { bold: true };
                      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
                      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

                      // Title row above (insert before adding data)
                      ws.spliceRows(1, 0, [`Price Trend — ${itemName}`]);
                      ws.mergeCells('A1:B1');
                      ws.getCell('A1').font = { bold: true, size: 13 };
                      ws.getCell('A1').alignment = { horizontal: 'center' };

                      // Sort newest first for the export
                      const sorted = [...logs].sort((a, b) => b.ts - a.ts);
                      sorted.forEach((log) => {
                        const iso = typeof log.date === 'string' ? log.date.replace(' ', 'T') : log.date;
                        const dt = new Date(iso);
                        const dateStr = !isNaN(dt.getTime())
                          ? dt.toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', hour12: true,
                            })
                          : String(log.date);
                        ws.addRow({ date: dateStr, rate: Number(log.rate) });
                      });

                      ws.eachRow((row) => {
                        row.eachCell((cell) => {
                          cell.border = {
                            top: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                            left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                            bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                            right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
                          };
                        });
                      });

                      const buf = await wb.xlsx.writeBuffer();
                      saveAs(new Blob([buf]), `price-trend-${itemName.replace(/\s+/g, '_')}.xlsx`);
                    }}
                    className="px-3 py-1.5 text-xs font-semibold bg-[#1978B5] text-white rounded-lg hover:bg-[#155f91] flex items-center gap-1.5"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                    </svg>
                    Download
                  </button>
                  <button onClick={() => setTrendModal(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {(() => {
                  const allLogs = trendData[trendModal.itemId] || [];
                  const periodMs = {
                    '7d': 7 * 24 * 60 * 60 * 1000,
                    '1m': 30 * 24 * 60 * 60 * 1000,
                    '3m': 90 * 24 * 60 * 60 * 1000,
                    '6m': 180 * 24 * 60 * 60 * 1000,
                  };
                  const now = Date.now();
                  const filteredLogs = trendPeriod === 'all'
                    ? allLogs
                    : allLogs.filter(l => l.ts && (now - l.ts) <= periodMs[trendPeriod]);
                  return (
                  <>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-xs text-gray-500 mr-1">Range:</span>
                      {[
                        { key: '7d', label: '7D' },
                        { key: '1m', label: '1M' },
                        { key: '3m', label: '3M' },
                        { key: '6m', label: '6M' },
                        { key: 'all', label: 'All' },
                      ].map(p => (
                        <button
                          key={p.key}
                          onClick={() => setTrendPeriod(p.key)}
                          className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                            trendPeriod === p.key
                              ? 'bg-[#1978B5] text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                      <span className="ml-auto text-[11px] text-gray-400">
                        {filteredLogs.length} of {allLogs.length} entries
                      </span>
                    </div>
                {allLogs.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm">No price history available.</div>
                ) : filteredLogs.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-sm">No entries in this range.</div>
                ) : (
                  <>
                    <div id="trend-chart-container" className="w-full h-64 bg-white border border-gray-100 rounded-lg p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={filteredLogs} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10 }}
                            tickFormatter={(d) => {
                              if (!d) return '';
                              const iso = typeof d === 'string' ? d.replace(' ', 'T') : d;
                              const dt = new Date(iso);
                              return isNaN(dt.getTime())
                                ? ''
                                : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
                            }}
                          />
                          <Tooltip
                            contentStyle={{ fontSize: '11px', padding: '4px 8px', borderRadius: '6px' }}
                            labelFormatter={(d) => {
                              if (!d) return '';
                              const iso = typeof d === 'string' ? d.replace(' ', 'T') : d;
                              const dt = new Date(iso);
                              return isNaN(dt.getTime())
                                ? String(d)
                                : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                            }}
                            formatter={(v) => [`₹ ${v}`, 'Rate']}
                          />
                          <Line type="monotone" dataKey="rate" stroke="#1978B5" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="space-y-2">
                      {[...filteredLogs].reverse().map((log, i) => {
                        const iso = typeof log.date === 'string' ? log.date.replace(' ', 'T') : log.date;
                        const dt = new Date(iso);
                        const formatted = !isNaN(dt.getTime())
                          ? dt.toLocaleString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit', hour12: true,
                            })
                          : log.date;
                        return (
                          <div key={i} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
                            <span className="inline-flex items-center gap-1.5 bg-[#1978B5] text-white text-xs font-semibold px-2.5 py-1 rounded-md">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {formatted}
                            </span>
                            <span className="text-sm font-semibold text-gray-800">₹ {log.rate}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                  </>
                  );
                })()}
              </div>

              <div className="px-5 py-3 border-t border-gray-100 flex justify-end flex-shrink-0">
                <button
                  onClick={() => setTrendModal(null)}
                  className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PriceUpdation;
